import { Plural, Trans, useLingui } from "@lingui/react/macro";
import type { Bot, BotSection, Group } from "@rakazo/contracts";
import { GROUP_NAME_MAX_LENGTH } from "@rakazo/contracts";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  RadioGroup,
  RadioGroupItem,
} from "@rakazo/ui-web";
import { useId, useState } from "react";
import { errorText } from "../../lib/user-error";

/** Each dialog is mounted only while open, so `open` is always true and the
 * parent unmounts it from `onCancel`. Escape and backdrop presses are ignored
 * while a request is in flight. */
function closeUnlessBusy(busy: boolean, onCancel: () => void) {
  return (open: boolean) => {
    if (!open && !busy) onCancel();
  };
}

export function NewBotSectionDialog({
  bot,
  onCancel,
  onConfirm,
}: {
  bot: Pick<Bot, "name">;
  onCancel: () => void;
  onConfirm: (name: string) => Promise<void>;
}) {
  const { t } = useLingui();
  const nameId = useId();
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <Dialog open onOpenChange={closeUnlessBusy(saving, onCancel)}>
      <DialogContent showCloseButton={false}>
        <form
          className="contents"
          onSubmit={(event) => {
            event.preventDefault();
            const trimmed = name.trim();
            if (!trimmed || saving) return;
            setSaving(true);
            setError(null);
            void onConfirm(trimmed).catch((err: unknown) => {
              setError(errorText(err, t`Could not create section`));
              setSaving(false);
            });
          }}
        >
          <DialogHeader>
            <DialogTitle>
              <Trans>New section</Trans>
            </DialogTitle>
            <DialogDescription>
              <Trans>Create a section and move {bot.name} into it.</Trans>
            </DialogDescription>
          </DialogHeader>
          <label htmlFor={nameId} className="block text-[13.5px] text-foreground/75">
            <Trans>Name</Trans>
            <Input
              id={nameId}
              maxLength={60}
              value={name}
              onChange={(event) => setName(event.target.value)}
              className="mt-2"
            />
          </label>
          {error ? <p className="text-[13.5px] text-destructive">{error}</p> : null}
          <DialogFooter>
            <Button type="button" variant="outline" disabled={saving} onClick={onCancel}>
              <Trans>Cancel</Trans>
            </Button>
            <Button type="submit" disabled={saving || !name.trim()}>
              {saving ? <Trans>Creating…</Trans> : <Trans>Create</Trans>}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function RenameBotSectionDialog({
  section,
  onCancel,
  onConfirm,
}: {
  section: Pick<BotSection, "name">;
  onCancel: () => void;
  onConfirm: (name: string) => Promise<void>;
}) {
  const { t } = useLingui();
  const nameId = useId();
  const [name, setName] = useState(section.name);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const trimmed = name.trim();
  const unchanged = trimmed === section.name;

  return (
    <Dialog open onOpenChange={closeUnlessBusy(saving, onCancel)}>
      <DialogContent showCloseButton={false} aria-describedby={undefined}>
        <form
          className="contents"
          onSubmit={(event) => {
            event.preventDefault();
            if (!trimmed || saving || unchanged) return;
            setSaving(true);
            setError(null);
            void onConfirm(trimmed).catch((err: unknown) => {
              setError(errorText(err, t`Could not rename section`));
              setSaving(false);
            });
          }}
        >
          <DialogHeader>
            <DialogTitle>
              <Trans>Rename section</Trans>
            </DialogTitle>
          </DialogHeader>
          <label htmlFor={nameId} className="block text-[13.5px] text-foreground/75">
            <Trans>Name</Trans>
            <Input
              id={nameId}
              maxLength={60}
              value={name}
              onChange={(event) => setName(event.target.value)}
              className="mt-2"
              autoFocus
            />
          </label>
          {error ? <p className="text-[13.5px] text-destructive">{error}</p> : null}
          <DialogFooter>
            <Button type="button" variant="outline" disabled={saving} onClick={onCancel}>
              <Trans>Cancel</Trans>
            </Button>
            <Button type="submit" disabled={saving || !trimmed || unchanged}>
              {saving ? <Trans>Saving…</Trans> : <Trans>Save</Trans>}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function ClearConversationDialog({
  bot,
  onCancel,
  onConfirm,
}: {
  bot: Pick<Bot, "name">;
  onCancel: () => void;
  onConfirm: () => Promise<void>;
}) {
  const { t } = useLingui();
  const [clearing, setClearing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <AlertDialog open onOpenChange={closeUnlessBusy(clearing, onCancel)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="break-words">
            <Trans>Clear {bot.name}’s conversation?</Trans>
          </AlertDialogTitle>
          <AlertDialogDescription>
            <Trans>
              This permanently removes every message and stops current work. The chat remains
              available.
            </Trans>
          </AlertDialogDescription>
        </AlertDialogHeader>
        {error ? <p className="text-[13.5px] text-destructive">{error}</p> : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={clearing}>
            <Trans>Cancel</Trans>
          </AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            disabled={clearing}
            onClick={() => {
              setClearing(true);
              setError(null);
              void onConfirm().catch((err: unknown) => {
                setError(errorText(err, t`Could not clear conversation`));
                setClearing(false);
              });
            }}
          >
            {clearing ? <Trans>Clearing…</Trans> : <Trans>Clear</Trans>}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function DeleteBotDialog({
  bot,
  onCancel,
  onConfirm,
}: {
  bot: Bot;
  onCancel: () => void;
  onConfirm: (deleteMemories: boolean) => Promise<void>;
}) {
  const { t } = useLingui();
  const [deleting, setDeleting] = useState(false);
  const [deleteMemories, setDeleteMemories] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const memoriesLabelId = useId();

  return (
    <AlertDialog open onOpenChange={closeUnlessBusy(deleting, onCancel)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="break-words">
            <Trans>Delete {bot.name}?</Trans>
          </AlertDialogTitle>
          <AlertDialogDescription>
            <Trans>
              Its conversation, files, and routines will be permanently deleted. Bots it created
              stay in your list.
            </Trans>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-2">
          <p id={memoriesLabelId} className="mb-2 text-[13.5px] text-foreground/75">
            <Trans>What about its memories?</Trans>
          </p>
          <RadioGroup
            aria-labelledby={memoriesLabelId}
            value={deleteMemories ? "delete" : "keep"}
            onValueChange={(value) => setDeleteMemories(value === "delete")}
          >
            {/* biome-ignore lint/a11y/noLabelWithoutControl: the wrapped RadioGroupItem is the control. */}
            <label className="flex cursor-pointer gap-3 rounded-lg border border-border p-3">
              <RadioGroupItem value="keep" className="mt-0.5" />
              <span>
                <span className="block text-[14px] text-foreground">
                  <Trans>Keep memories</Trans>
                </span>
                <span className="mt-0.5 block text-[12.5px] text-muted-foreground">
                  <Trans>Move them to your shared memory.</Trans>
                </span>
              </span>
            </label>
            {/* biome-ignore lint/a11y/noLabelWithoutControl: the wrapped RadioGroupItem is the control. */}
            <label className="flex cursor-pointer gap-3 rounded-lg border border-border p-3">
              <RadioGroupItem value="delete" className="mt-0.5" />
              <span>
                <span className="block text-[14px] text-foreground">
                  <Trans>Delete memories too</Trans>
                </span>
                <span className="mt-0.5 block text-[12.5px] text-muted-foreground">
                  <Trans>This cannot be undone.</Trans>
                </span>
              </span>
            </label>
          </RadioGroup>
        </div>
        {error ? <p className="text-[13.5px] text-destructive">{error}</p> : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleting}>
            <Trans>Cancel</Trans>
          </AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            disabled={deleting}
            onClick={() => {
              setDeleting(true);
              setError(null);
              void onConfirm(deleteMemories).catch((err: unknown) => {
                setError(errorText(err, t`Could not delete bot`));
                setDeleting(false);
              });
            }}
          >
            {deleting ? <Trans>Deleting…</Trans> : <Trans>Delete</Trans>}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** The space the Space settings section acts on. */
export type SettingsSpace = {
  id: string;
  name: string;
  canRename?: boolean;
  canDelete?: boolean;
  companyPlatform?: "tiktok" | "ozon" | null;
  /** Every bot deleting the space removes, from the server. */
  botCount?: number;
};
/** Names what goes with the space: its bots, and with them their chats and groups. */
export function DeleteSpaceDialog({
  space,
  onCancel,
  onConfirm,
}: {
  space: SettingsSpace;
  onCancel: () => void;
  onConfirm: () => Promise<void>;
}) {
  const botCount = space.botCount ?? 0;
  const name = space.name;
  return (
    <DeleteItemDialog
      item={space}
      noun={space.companyPlatform ? "company" : "space"}
      description={
        space.companyPlatform ? (
          <Trans>
            This deletes the company, its members and all their data. This cannot be undone.
          </Trans>
        ) : undefined
      }
      title={
        space.companyPlatform ? undefined : botCount > 0 ? (
          <Plural
            value={botCount}
            one={`Delete ${name} and its # bot?`}
            other={`Delete ${name} and its # bots?`}
          />
        ) : undefined
      }
      onCancel={onCancel}
      onConfirm={onConfirm}
    />
  );
}

export function CompanySettingsDialog({
  group,
  onCancel,
  onSave,
  onDelete,
}: {
  group: Pick<Group, "name" | "isCompany">;
  onCancel: () => void;
  onSave: (name: string) => Promise<void>;
  onDelete: () => void;
}) {
  const { t } = useLingui();
  const nameId = useId();
  const [name, setName] = useState(group.name);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const trimmed = name.trim();

  return (
    <Dialog open onOpenChange={closeUnlessBusy(saving, onCancel)}>
      <DialogContent>
        <form
          className="contents"
          onSubmit={(event) => {
            event.preventDefault();
            if (!trimmed || saving) return;
            setSaving(true);
            setError(null);
            void onSave(trimmed).catch((cause: unknown) => {
              setError(errorText(cause, t`Could not save company`));
              setSaving(false);
            });
          }}
        >
          <DialogHeader>
            <DialogTitle>
              <Trans>Company</Trans>
            </DialogTitle>
          </DialogHeader>
          <label htmlFor={nameId} className="block text-[13.5px] text-foreground/75">
            <Trans>Company name</Trans>
            <Input
              id={nameId}
              value={name}
              maxLength={GROUP_NAME_MAX_LENGTH}
              autoFocus
              className="mt-2"
              onChange={(event) => setName(event.target.value)}
            />
          </label>
          {error ? <p className="text-[13.5px] text-destructive">{error}</p> : null}
          <DialogFooter className="sm:justify-between">
            {group.isCompany === true ? (
              <Button type="button" variant="destructive" disabled={saving} onClick={onDelete}>
                <Trans>Delete company</Trans>
              </Button>
            ) : null}
            <Button type="submit" disabled={saving || !trimmed}>
              {saving ? <Trans>Saving…</Trans> : <Trans>Save</Trans>}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function DeleteItemDialog({
  item,
  noun,
  title,
  description,
  onCancel,
  onConfirm,
}: {
  item: { name: string };
  noun: "company" | "group" | "routine" | "section" | "space";
  title?: React.ReactNode;
  description?: React.ReactNode;
  onCancel: () => void;
  onConfirm: () => Promise<void>;
}) {
  const { t } = useLingui();
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <AlertDialog open onOpenChange={closeUnlessBusy(deleting, onCancel)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title ?? <Trans>Delete {item.name}?</Trans>}</AlertDialogTitle>
          <AlertDialogDescription>
            {description ?? <Trans>This cannot be undone.</Trans>}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {error ? <p className="text-[13.5px] text-destructive">{error}</p> : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleting}>
            <Trans>Cancel</Trans>
          </AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            disabled={deleting}
            onClick={() => {
              setDeleting(true);
              setError(null);
              void onConfirm().catch((err: unknown) => {
                setError(
                  errorText(
                    err,
                    noun === "company"
                      ? t`Could not delete company`
                      : noun === "group"
                        ? t`Could not delete group`
                        : noun === "space"
                          ? t`Could not delete space`
                          : noun === "section"
                            ? t`Could not delete section`
                            : t`Could not delete routine`,
                  ),
                );
                setDeleting(false);
              });
            }}
          >
            {deleting ? <Trans>Deleting…</Trans> : <Trans>Delete</Trans>}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
